import React, { useEffect, useState } from 'react';

import Box from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Drawer from '@liquidweb-dev/undercurrent/Drawer';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import LWForm from 'components/LWForm';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';
import useAccountUserStatedataListforkey from 'modules/queries/account/user/stateData/useListForKey';
import useAssetDetails from 'modules/queries/asset/useDetails';
import { useUpdateHostname } from 'modules/queries/server/useUpdate';
import { validateDomain } from 'utility/lwForm/validators';
import { HookedSubmit, HookedTextField } from '../Form';

/**
 * @param {object} props
 * @param {string} props.domain
 * @param {() => void} props.onClose
 * @param {string} props.uniqId
 * */
const EditServerHostnameForm = ({ domain, onClose, uniqId }) => {
	const [formValues, setFormValues] = useState({ domain });
	const [formErrors, setFormErrors] = useState({});

	const { isLoading: isFavoritesLoading } = useAccountUserStatedataListforkey({
		key: 'favorites',
	});

	useEffect(() => {
		if (domain) {
			setFormValues((prev) => ({ ...prev, domain }));
		}
	}, [domain]);

	const { mutate: updateHostname, isLoading: updateHostnameIsLoading } =
		useUpdateHostname();

	const handleSubmit = (vals) => {
		updateHostname(
			{
				uniqId,
				domain: vals.domain,
			},
			{
				onSuccess: onClose,
			},
		);
	};

	return (
		<LWForm
			values={formValues}
			onValuesChange={setFormValues}
			errors={formErrors}
			onErrorsChange={setFormErrors}
			onSubmit={handleSubmit}
			validators={{ domain: validateDomain }}
			style={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				height: '100%',
			}}
		>
			<DrawerContent sx={{ gap: 300 }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 200 }}>
					<Typography>What will this do?</Typography>
					<Typography>
						Updating your server&apos;s hostname will change the unique name
						assigned to your server within the Liquid Web platform. It will not
						affect your domain name, but may need to be updated in your DNS
						records.
					</Typography>
				</Box>
				<HookedTextField
					name="domain"
					label="Server hostname"
					helperText="Cannot contain spaces or special characters"
				/>
			</DrawerContent>
			<DrawerFooter sx={{ display: 'flex', flexDirection: 'column', gap: 100 }}>
				<HookedSubmit
					disabled={isFavoritesLoading}
					loading={updateHostnameIsLoading}
					size="large"
				>
					Update
				</HookedSubmit>
				<Button onClick={onClose} variant="ghost" size="large">
					Cancel
				</Button>
			</DrawerFooter>
		</LWForm>
	);
};

/** @param {Omit<import('@liquidweb-dev/undercurrent/Drawer').DrawerProps, 'onClose'> & { onClose: () => void, uniqId: string }} drawerProps */
const DrawerEditServerHostname = ({
	onClose,
	uniqId,
	open,
	...drawerProps
}) => {
	const { data: assetDetailsData } = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: hostingDetailsAlsowith,
		},
		{ enabled: Boolean(open) },
	);

	const { domain } = assetDetailsData || {};

	return (
		<Drawer anchor="right" onClose={onClose} open={open} {...drawerProps}>
			<DrawerHeader onClose={onClose}>
				<Typography
					variant="title-xs"
					sx={{
						fontWeight: 500,
					}}
				>
					Update hostname
				</Typography>
				<Typography variant="body-sm">
					Change the hostname used by this sever
				</Typography>
			</DrawerHeader>
			<EditServerHostnameForm
				domain={domain || ''}
				onClose={onClose}
				uniqId={uniqId}
			/>
		</Drawer>
	);
};

export default DrawerEditServerHostname;
